import { useEffect, useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import SEO from '@/components/SEO';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  MapPin, 
  Phone, 
  Mail, 
  Globe, 
  Shield,
  User,
  ArrowLeft,
  Linkedin,
  Building2
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { getSpecialtyLabel } from '@/lib/specialtyLabels';

interface ProfessionalData {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  company_name: string;
  company_website: string;
  specialties: string[];
  service_areas: string[];
  experience_years: string;
  directory_slug: string;
  profession: string;
  license_state: string;
  availability: string;
  areas_of_expertise: string[];
  service_city: string;
  service_zip_code: string;
  website_url: string;
  professional_photo_url?: string;
  bio?: string;
  service_radius_miles?: number;
  role_title?: string;
  linkedin_url?: string;
  license_number?: string;
  license_verification_status?: string;
}

export default function ProfessionalProfile() {
  const { slug } = useParams<{ slug: string }>();
  const [professional, setProfessional] = useState<ProfessionalData | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const loadProfessional = async () => {
      if (!slug) return;
      
      try {
        const { data, error } = await supabase.functions.invoke('get-professional-directory', {
          body: { slug },
        });
        
        if (error) {
          console.error('Failed to load professional:', error);
          setNotFound(true);
        } else if (data?.professional) {
          setProfessional(data.professional);
        } else {
          setNotFound(true);
        }
      } catch (error) {
        console.error('Failed to load professional:', error);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };

    loadProfessional();
  }, [slug]);

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-10">
        <div className="text-center text-muted-foreground">Loading professional profile...</div>
      </div>
    );
  }

  if (notFound || !professional) {
    return <Navigate to="/professionals/directory" replace />;
  }

  const fullName = `${professional.first_name} ${professional.last_name}`;

  const getLocationDisplay = () => {
    const parts = [
      professional.service_city,
      professional.license_state,
      professional.service_zip_code
    ].filter(Boolean);
    
    if (parts.length === 0) return null;
    return parts.join(', ');
  };

  const getServiceRadiusDisplay = () => {
    if (professional.service_radius_miles) {
      return `${professional.service_radius_miles} mile service radius`;
    }
    return null;
  };

  const formatUrl = (url: string) => {
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      return `https://${url}`;
    }
    return url;
  };

  const getSpecialties = () => {
    const specialties = professional.specialties || professional.areas_of_expertise || [];
    return specialties.filter(Boolean);
  };

  const isVerified = professional.license_verification_status === 'verified';

  const professionDisplayNames: Record<string, string> = {
    'Occupational Therapist': 'Occupational Therapist',
    'Physical Therapist': 'Physical Therapist',
    'Social Worker': 'Social Worker',
    'Financial Advisor': 'Financial Advisor',
    'Elder Law Attorney': 'Elder Law Attorney',
    'Home Care Agency': 'Home Care Agency',
    'Geriatric Care Manager': 'Geriatric Care Manager',
    'Other': 'Senior Care Professional',
    'Independent Caregiver': 'Independent Caregiver',
    'OT': 'Occupational Therapist',
    'CAPS': 'Certified Aging-in-Place Specialist',
    'PROFESSIONAL': 'Senior Care Professional',
  };

  const websiteUrl = professional.website_url || professional.company_website;

  return (
    <main className="container mx-auto px-4 py-10 max-w-5xl">
      <SEO
        title={`${fullName} - ${professionDisplayNames[professional.profession] || professional.profession}`}
        description={`${fullName} is a ${professionDisplayNames[professional.profession] || professional.profession}${professional.license_state ? ` serving ${professional.license_state}` : ''}. ${professional.bio || 'View contact details and specialties.'}`}
        canonical={`https://seniorthrive.com/professionals/${slug}`}
      />

      {/* Back Navigation */}
      <div className="mb-6">
        <Link 
          to="/professionals/directory" 
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors text-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Directory
        </Link>
      </div>

      <div className="grid gap-8 lg:grid-cols-3">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Profile Header Card */}
          <Card className="rounded-2xl border bg-white shadow-sm overflow-hidden">
            <CardHeader className="pb-0">
              <div className="flex items-start gap-5">
                {professional.professional_photo_url ? (
                  <img
                    src={professional.professional_photo_url}
                    alt={`${fullName} profile`}
                    className="w-20 h-20 rounded-full object-cover border-2 border-border shrink-0"
                  />
                ) : (
                  <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center border-2 border-border shrink-0">
                    <User className="w-10 h-10 text-muted-foreground" />
                  </div>
                )}
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-start gap-3 flex-wrap mb-1">
                    <h1 className="text-2xl font-bold text-foreground">
                      {fullName}
                    </h1>
                    {isVerified && (
                      <Badge className="bg-st_light_blue text-white shrink-0">
                        <Shield className="w-3 h-3 mr-1" />
                        Verified
                      </Badge>
                    )}
                  </div>

                  {/* Role Title - italic subtitle */}
                  {professional.role_title && (
                    <p className="text-muted-foreground italic mb-1">
                      {professional.role_title}
                    </p>
                  )}
                  
                  <p className="text-primary font-medium mb-3">
                    {professionDisplayNames[professional.profession] || professional.profession}
                  </p>

                  {/* Location & Service Radius */}
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
                    {getLocationDisplay() && (
                      <div className="flex items-center gap-1.5">
                        <MapPin className="w-4 h-4" />
                        <span>{getLocationDisplay()}</span>
                      </div>
                    )}
                    {getServiceRadiusDisplay() && (
                      <span className="text-muted-foreground/70">
                        • {getServiceRadiusDisplay()}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </CardHeader>

            <CardContent className="pt-6 space-y-6">
              {/* Bio */}
              {professional.bio && (
                <div>
                  <h2 className="text-sm font-semibold text-st_taupe uppercase tracking-wide mb-2">About</h2>
                  <p className="text-foreground leading-relaxed">
                    {professional.bio}
                  </p>
                </div>
              )}

              {/* Specialties */}
              {getSpecialties().length > 0 && (
                <div>
                  <h2 className="text-sm font-semibold text-st_taupe uppercase tracking-wide mb-3">Areas of Focus</h2>
                  <div className="flex flex-wrap gap-2">
                    {getSpecialties().map((specialty) => (
                      <Badge key={specialty} variant="secondary" className="text-sm">
                        {getSpecialtyLabel(specialty)}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Company */}
              {professional.company_name && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Building2 className="w-4 h-4" />
                  <span>{professional.company_name}</span>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Contact Sidebar */}
        <div className="lg:col-span-1">
          <Card className="rounded-2xl border bg-white shadow-sm sticky top-6">
            <CardHeader className="pb-4">
              <h2 className="text-lg font-semibold">Get in Touch</h2>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Primary CTA */}
              {professional.email && (
                <Button 
                  size="lg" 
                  className="w-full bg-st_light_blue hover:bg-st_light_blue/90"
                  asChild
                >
                  <a href={`mailto:${professional.email}`}>
                    <Mail className="w-4 h-4 mr-2" />
                    Request Consultation
                  </a>
                </Button>
              )}
              
              {/* Phone */}
              {professional.phone && (
                <Button 
                  variant="outline" 
                  size="lg" 
                  className="w-full"
                  onClick={() => window.open(`tel:${professional.phone}`, '_blank')}
                >
                  <Phone className="w-4 h-4 mr-2" />
                  {professional.phone}
                </Button>
              )}
              
              {/* Website */}
              {websiteUrl && (
                <Button 
                  variant="outline" 
                  size="lg" 
                  className="w-full"
                  onClick={() => window.open(formatUrl(websiteUrl), '_blank')}
                >
                  <Globe className="w-4 h-4 mr-2" />
                  Visit Website
                </Button>
              )}

              {/* LinkedIn */}
              {professional.linkedin_url && (
                <Button 
                  variant="outline" 
                  size="lg" 
                  className="w-full"
                  onClick={() => window.open(formatUrl(professional.linkedin_url!), '_blank')}
                >
                  <Linkedin className="w-4 h-4 mr-2" />
                  LinkedIn Profile
                </Button>
              )}

              {/* License Info */}
              {professional.license_state && (
                <div className="pt-4 mt-4 border-t border-border/50">
                  <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">Licensed In</p>
                  <p className="text-sm font-medium">{professional.license_state}</p>
                  {professional.license_number && (
                    <p className="text-xs text-muted-foreground mt-1">
                      License #{professional.license_number}
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </main>
  );
}
